import { Divider, Select, Radio, DatePicker } from "antd";
import Title from "antd/es/typography/Title";
import dayjs, { Dayjs } from "dayjs";
import { useFormikContext } from "formik";
import React, { FC, useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { selectFiltering } from "shared/helpers";
import { REPORT_DATE_TIME_FORMAT, REPORT_TIME_FORMAT } from "shared/constants";
import { Colors } from "shared/themes";
import { AdaptedValues, ExecuteReportValues } from "shared/types";
import {
  useGetMaterialCategoriesWithNoParamsQuery,
  useGetAllMaterialsNoPaginationQuery,
} from "shared/api";
import { useGetAllCompaniesQuery } from "shared/api/suppliers/suppliersApi";
import type { MaterialCategoriesItem, ReportItem } from "shared/api";
import { ReportFormatSelect } from "./ReportFormatSelect";

interface QualityAdhesionFilterProps {
  adaptedOrders: AdaptedValues[];
  selectedOrderId: number | null;
  onOrderChange: (value: number | null) => void;
  onOrderSearch: (value: string) => void;
  isLoadingOrders: boolean;
  clearPdfUrl?: () => void;
  selectedReport?: ReportItem;
}

type OptionType = "material" | "order" | "company";

export const QualityAdhesionFilter: FC<QualityAdhesionFilterProps> = ({
  adaptedOrders,
  selectedOrderId,
  onOrderChange,
  onOrderSearch,
  isLoadingOrders,
  clearPdfUrl,
  selectedReport,
}) => {
  const { t } = useTranslation("", { keyPrefix: "executeReports" });
  const { values, setFieldValue } = useFormikContext<ExecuteReportValues>();

  const [selectedOption, setSelectedOption] = useState<OptionType>("material");
  const [selectedMaterialCategoryId, setSelectedMaterialCategoryId] = useState<
    number | null
  >(null);

  const { data: materialCategories, isLoading: isLoadingCategories } =
    useGetMaterialCategoriesWithNoParamsQuery(undefined);

  const { data: materials, isLoading: isLoadingMaterials } =
    useGetAllMaterialsNoPaginationQuery(
      selectedMaterialCategoryId
        ? { material_category: selectedMaterialCategoryId }
        : undefined,
      { skip: !selectedMaterialCategoryId }
    );

  const { data: companies, isLoading: isLoadingCompanies } =
    useGetAllCompaniesQuery({});

  const adaptedMaterialCategories = useMemo(() => {
    return (materialCategories ?? []).map((cat: MaterialCategoriesItem) => ({
      label: cat.name,
      value: cat.id,
    }));
  }, [materialCategories]);

  const adaptedMaterials = useMemo(() => {
    return (materials ?? []).map((material) => ({
      label: material.name,
      value: material.id,
    }));
  }, [materials]);

  const adaptedCompanies = useMemo(() => {
    return (companies ?? []).map((company) => ({
      label: company.name,
      value: company.id,
    }));
  }, [companies]);

  const selectedMaterialCategory = useMemo(() => {
    return materialCategories?.find(
      (cat: MaterialCategoriesItem) => cat.id === selectedMaterialCategoryId
    );
  }, [materialCategories, selectedMaterialCategoryId]);

  const dynamicFieldOptions = useMemo(() => {
    if (!selectedMaterialCategory?.config_dynamic_tracker_fields) {
      return [];
    }

    const options: { label: string; value: string }[] = [];
    selectedMaterialCategory.config_dynamic_tracker_fields.forEach((config) => {
      config.fields.forEach((field) => {
        const fieldValue = `${config.title}.${field.name}`;
        options.push({
          label: fieldValue,
          value: fieldValue,
        });
      });
    });

    return options;
  }, [selectedMaterialCategory]);

  const handleOptionChange = (option: OptionType) => {
    setSelectedOption(option);

    if (option !== "order") {
      setFieldValue("order_id", null);
      onOrderChange(null);
    }
    if (option !== "material") {
      setFieldValue("material_id", null);
    }
    if (option !== "company") {
      setFieldValue("company_id", null);
      setFieldValue("timestamp_start", null);
      setFieldValue("timestamp_end", null);
    }

    setFieldValue("dynamic_tracker_fields", []);
  };

  const handleMaterialCategoryChange = (value: number) => {
    setSelectedMaterialCategoryId(value);
    setFieldValue("material_category_id", value);
    setFieldValue("material_id", null);
    setFieldValue("dynamic_tracker_fields", []);
  };

  const handleMaterialChange = (value: number) => {
    setFieldValue("material_id", value);
  };

  const handleOrderChange = (value: number) => {
    onOrderChange(value);
    setFieldValue("order_id", value);
  };

  const handleCompanyChange = (value: number | null) => {
    setFieldValue("company_id", value);
  };

  const handleTimestampChange = (
    field: "timestamp_start" | "timestamp_end",
    val: Dayjs | null
  ) => {
    setFieldValue(field, val ? val.toISOString() : null);
  };

  const handleDynamicFieldsChange = (selectedFields: string[]) => {
    setFieldValue("dynamic_tracker_fields", selectedFields);
  };

  useEffect(() => {
    if (selectedMaterialCategoryId && selectedMaterialCategory) {
      setFieldValue("material_category_id", selectedMaterialCategoryId);
    }
  }, [selectedMaterialCategoryId, selectedMaterialCategory, setFieldValue]);

  return (
    <>
      <Divider className="my-1.5" style={{ background: Colors.blue.primary }} />

      <div className="flex flex-col gap-y-3">
        <div className="flex gap-x-4">
          <Radio.Group
            value={selectedOption}
            onChange={(e) => handleOptionChange(e.target.value)}
          >
            <Radio value="material">
              Option A: Material Category → Material
            </Radio>
            <Radio value="order">Option B: Order</Radio>
            <Radio value="company">Option C: Company</Radio>
          </Radio.Group>
        </div>

        {/* Material Category is shared across all options */}
        <div className="flex gap-x-2">
          <Title level={5} className="!m-0 !text-start">
            Material Category
          </Title>
          {" - "}
          <Select
            loading={isLoadingCategories}
            value={selectedMaterialCategoryId}
            placeholder="Select Material Category"
            options={adaptedMaterialCategories}
            onChange={handleMaterialCategoryChange}
            className="w-[300px]"
            size="small"
            showSearch
            filterOption={(input, option) => selectFiltering(input, option)}
          />
        </div>

        {selectedOption === "material" && selectedMaterialCategoryId && (
          <div className="flex gap-x-2">
            <Title level={5} className="!m-0 !text-start">
              Material
            </Title>
            {" - "}
            <Select
              loading={isLoadingMaterials}
              value={values.material_id}
              placeholder="Select Material"
              options={adaptedMaterials}
              onChange={handleMaterialChange}
              className="w-[300px]"
              size="small"
              showSearch
              filterOption={(input, option) => selectFiltering(input, option)}
            />
          </div>
        )}

        {selectedOption === "order" && selectedMaterialCategoryId && (
          <div className="flex flex-col gap-y-0.5">
            <div className="flex gap-x-2">
              <Title level={5} className="whitespace-nowrap !m-0 !text-start">
                {t("selectOrder")}
              </Title>
              {" - "}
              <Select
                loading={isLoadingOrders}
                value={selectedOrderId}
                placeholder={t("selectOrder")}
                options={adaptedOrders}
                onChange={handleOrderChange}
                onSearch={onOrderSearch}
                className="w-[300px]"
                size="small"
                showSearch
                filterOption={false}
              />
            </div>
            <span className="text-[10px] leading-tight text-gray-400">
              * {t("orderSearchHint")}
            </span>
          </div>
        )}

        {selectedOption === "company" && (
          <>
            <div className="flex gap-x-2">
              <Title level={5} className="!m-0 !text-start">
                {t("selectCompany")}
              </Title>
              {" - "}
              <Select
                loading={isLoadingCompanies}
                value={values.company_id}
                placeholder={t("selectCompany")}
                options={adaptedCompanies}
                onChange={handleCompanyChange}
                className="w-[300px]"
                size="small"
                allowClear
                showSearch
                filterOption={(input, option) => selectFiltering(input, option)}
              />
            </div>

            <div className="flex gap-x-3">
              <div className="flex gap-x-2">
                <Title level={5} className="!m-0 !text-start">
                  {t("startDate")}
                </Title>
                {" - "}
                <DatePicker
                  showTime={{ format: REPORT_TIME_FORMAT }}
                  format={REPORT_DATE_TIME_FORMAT}
                  size="small"
                  value={
                    values.timestamp_start
                      ? dayjs(values.timestamp_start)
                      : null
                  }
                  onChange={(val) =>
                    handleTimestampChange("timestamp_start", val)
                  }
                  onOk={(val) => handleTimestampChange("timestamp_start", val)}
                />
              </div>

              <Divider
                type="vertical"
                className="h-full"
                style={{ background: Colors.blue.primary }}
              />

              <div className="flex gap-x-2">
                <Title level={5} className="!m-0 !text-start">
                  {t("endDate")}
                </Title>
                {" - "}
                <DatePicker
                  showTime={{ format: REPORT_TIME_FORMAT }}
                  format={REPORT_DATE_TIME_FORMAT}
                  size="small"
                  value={
                    values.timestamp_end ? dayjs(values.timestamp_end) : null
                  }
                  onChange={(val) =>
                    handleTimestampChange("timestamp_end", val)
                  }
                  onOk={(val) => handleTimestampChange("timestamp_end", val)}
                />
              </div>
            </div>
          </>
        )}

        {selectedMaterialCategoryId && dynamicFieldOptions.length > 0 && (
          <div className="flex gap-x-2 items-start">
            <Title level={5} className="!m-0 !text-start">
              Dynamic Fields
            </Title>
            {" - "}
            <Select
              mode="multiple"
              value={values.dynamic_tracker_fields}
              placeholder="Select Dynamic Tracker Fields"
              options={dynamicFieldOptions}
              onChange={handleDynamicFieldsChange}
              className="w-[500px]"
              size="small"
              showSearch
              filterOption={(input, option) => selectFiltering(input, option)}
            />
          </div>
        )}

        <ReportFormatSelect
          selectedReport={selectedReport}
          clearPdfUrl={clearPdfUrl}
        />
      </div>

      <Divider className="my-1.5" style={{ background: Colors.blue.primary }} />
    </>
  );
};
